import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useWindowEvent } from '../../hooks/useWindowEvent';
import { getTestProps } from '../../util/testProps';
import { stringifyParams } from '../../util/urls';
import qs from 'query-string';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { AlertTriangle } from '@lifeomic/chromicons';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { useSelector } from 'react-redux';
import { configSelectors } from '../../redux/modules/config';

export const LEGACY_LIFEOLOGY_COURSE_DOMAIN = 'lifeology.us.lifeomic.com';
export const LEGACY_LIFEOLOGY_COURSE_ORIGIN_PREFIX = `https://${LEGACY_LIFEOLOGY_COURSE_DOMAIN}/`;
export const LIFEOLOGY_COURSE_DOMAIN = 'app.us.lifeology.io';
export const LIFEOLOGY_COURSE_ORIGIN_PREFIX = `https://${LIFEOLOGY_COURSE_DOMAIN}/`;

export enum CourseState {
  Initializing = 'olo:status:init', // Lifeology code is running
  Loaded = 'olo:status:load', // Assets have been preloaded
  Exited = 'olo:status:exit', // The user exited the course via X
  FeedbackSubmitted = 'olo:status:done', // The user submitted feedback form
  LastPageLoaded = 'olo:status:last', // The last slide was loaded
}

export const testIds = {
  iframe: 'lifeologyCourse-iframe',
};

const messages = defineMessages({
  noCourseConfigured: {
    id: 'lifeologyCourse.noCourseConfigured',
    defaultMessage: 'No valid Lifeology course configured.',
  },
  iframeTitle: {
    id: 'lifeologyCourse.iframeTitle',
    defaultMessage: 'Lifeology Course',
  },
});

const useStyles = makeStyles(() => ({
  iframeStyle: {
    width: '100%',
    height: '100%',
  },
}));

export type LifeologyCourseProps = {
  url: string;
  onCourseStateUpdate?: (state: CourseState) => any;
  className?: string;
};

export const LifeologyCourse = (props: LifeologyCourseProps) => {
  const { onCourseStateUpdate } = props;
  const lifeologyIoBaseUri = `${useSelector(
    state => configSelectors.selectConfigState(state).lifeologyIoBaseUri,
  )}/`;
  const isValidDomain = useCallback(
    (url: string): boolean => {
      try {
        const asUrl = new URL(url);
        const lifeologyUriAsUrl = new URL(lifeologyIoBaseUri);
        return (
          asUrl.host === lifeologyUriAsUrl.host ||
          asUrl.host === LEGACY_LIFEOLOGY_COURSE_DOMAIN ||
          asUrl.host === LIFEOLOGY_COURSE_DOMAIN
        );
      } catch (e) {
        console.error('Error occurred validating domain', e);
        return false;
      }
    },
    [lifeologyIoBaseUri],
  );

  const messageHandler = useCallback(
    (event: MessageEvent) => {
      const originUrl = event?.origin + '/';
      if (
        (!originUrl?.startsWith(lifeologyIoBaseUri) &&
          !originUrl?.startsWith(LEGACY_LIFEOLOGY_COURSE_ORIGIN_PREFIX)) ||
        !isValidDomain(event.origin)
      ) {
        return;
      }

      if (
        onCourseStateUpdate &&
        [
          CourseState.Initializing,
          CourseState.Loaded,
          CourseState.Exited,
          CourseState.FeedbackSubmitted,
          CourseState.LastPageLoaded,
        ].includes(event.data)
      ) {
        onCourseStateUpdate(event.data);
      }
    },
    [onCourseStateUpdate, isValidDomain, lifeologyIoBaseUri],
  );
  useWindowEvent('message', messageHandler);
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  let courseUrl = props.url || '';

  if (
    (!courseUrl.startsWith(lifeologyIoBaseUri) &&
      !courseUrl.startsWith(LEGACY_LIFEOLOGY_COURSE_ORIGIN_PREFIX) &&
      !courseUrl.startsWith(LIFEOLOGY_COURSE_ORIGIN_PREFIX)) ||
    !isValidDomain(courseUrl)
  ) {
    return (
      <Box align="center" fullHeight fullWidth justify="center">
        <Alert statusType="warning">
          <AlertIcon icon={AlertTriangle} />
          <AlertBody>
            <Text size="subbody">
              {formatMessage(messages.noCourseConfigured)}
            </Text>
          </AlertBody>
        </Alert>
      </Box>
    );
  }

  const fragmentIndex = courseUrl.indexOf('#');
  if (fragmentIndex > -1) {
    courseUrl = courseUrl.substring(0, fragmentIndex);
  }

  const queryIndex = courseUrl.indexOf('?');
  let existingParams = {};
  if (queryIndex > -1) {
    courseUrl = courseUrl.substring(0, queryIndex);
    existingParams = qs.parse(props.url.substring(queryIndex)) || {};
  }

  const queryParams = {
    ...existingParams,
    emit: 1,
  };
  courseUrl = `${courseUrl}${stringifyParams(queryParams)}`;

  return (
    <iframe
      {...getTestProps(testIds.iframe)}
      className={clsx(classes.iframeStyle, props.className)}
      src={courseUrl}
      frameBorder="0"
      title={formatMessage(messages.iframeTitle)}
    />
  );
};
